/**
 * PortalMessagesPanel — interne team plaatst berichten die de klant in zijn
 * portal-timeline (/Api/portal/timeline) ziet.
 *
 * Auth: existing Azure AD via authFetch.
 * Backend: /Api/portal-messages (GET, POST, DELETE).
 * Visuele stijl: Stitch design tokens (zie ProjectActivityFeed).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { bevestig } from '../../utils/bevestig';
import { Box, Typography, TextField, CircularProgress } from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  tertiary: '#635983',
  surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  fontBody: '"Inter", system-ui, sans-serif',
};

const ACCENT = '#7c5fdf';        // klant-communicatie accent (paars-ish, niet de standaard interne)
const ACCENT_BG = '#f0ebff';
const ACCENT_BORDER = '#d5c8f5';

const Icon = ({ name, size = 20, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const MAX_BODY = 4000;
const MAX_SUBJECT = 200;

export default function PortalMessagesPanel({ projectId, currentUserEmail, authFetch }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);

  const fetchMessages = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/portal-messages?project_id=${encodeURIComponent(projectId)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, authFetch]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handlePost = async (e) => {
    e?.preventDefault?.();
    if (!body.trim()) return;
    setPosting(true);
    setError(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/portal-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId,
          subject: subject.trim() || null,
          body: body.trim(),
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${res.status}`);
      }
      setSubject('');
      setBody('');
      await fetchMessages();
    } catch (e) {
      setError(e.message);
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await bevestig({ titel: 'Bericht verwijderen?', tekst: 'Klanten zien het direct niet meer.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/portal-messages/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${res.status}`);
      }
      await fetchMessages();
    } catch (e) {
      setError(e.message);
    }
  };

  if (!projectId) return null;

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const day = new Date(d); day.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today - day) / 86400000);
    const timeStr = d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 0) return `Vandaag · ${timeStr}`;
    if (diffDays === 1) return `Gisteren · ${timeStr}`;
    return `${d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })} · ${timeStr}`;
  };

  const stitchInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '0.5rem',
      backgroundColor: T.surface,
      fontFamily: T.fontBody,
      fontSize: '0.85rem',
      '& fieldset': { borderColor: T.outlineVariant, borderWidth: '1px' },
      '&:hover fieldset': { borderColor: T.outline },
      '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root': {
      fontFamily: T.fontBody,
      fontSize: '0.82rem',
      color: T.outline,
      '&.Mui-focused': { color: ACCENT },
    },
    '& .MuiFormHelperText-root': {
      fontFamily: T.fontBody,
      fontSize: '0.7rem',
      color: T.outlineVariant,
    },
  };

  return (
    <Box sx={{
      mt: 3,
      borderRadius: '0.75rem',
      bgcolor: T.surface,
      border: `1px solid ${T.outlineVariant}`,
      overflow: 'hidden',
      fontFamily: T.fontBody,
    }}>
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 1.75,
        display: 'flex', alignItems: 'center', gap: 1.25,
        borderBottom: `1px solid ${T.outlineVariant}`,
        bgcolor: T.surfaceLow,
      }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: '0.5rem',
          bgcolor: ACCENT_BG, border: `1px solid ${ACCENT_BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="forum" size={18} sx={{ color: ACCENT }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.95rem', fontWeight: 600, color: T.text, lineHeight: 1.2 }}>
            Berichten naar klant
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, mt: 0.25 }}>
            Wordt zichtbaar in de portal-tijdlijn van de klant
          </Typography>
        </Box>
        <Box sx={{
          px: 0.875, py: 0.25, borderRadius: '9999px',
          bgcolor: ACCENT_BG, border: `1px solid ${ACCENT_BORDER}`,
          display: 'flex', alignItems: 'center', gap: 0.5,
        }}>
          <Icon name="visibility" size={14} sx={{ color: ACCENT }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600, color: ACCENT, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            zichtbaar voor klant
          </Typography>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2.5 }}>
        {error && (
          <Box sx={{
            mb: 2, p: 1.25, borderRadius: '0.5rem',
            bgcolor: '#fef2f2', border: '1px solid #fecaca',
            display: 'flex', alignItems: 'flex-start', gap: 1,
          }}>
            <Icon name="error" size={18} sx={{ color: '#dc2626', mt: 0.1 }} />
            <Typography sx={{ flex: 1, fontFamily: T.fontBody, fontSize: '0.8rem', color: '#b91c1c' }}>
              {error}
            </Typography>
            <Box onClick={() => setError(null)} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <Icon name="close" size={16} sx={{ color: '#b91c1c' }} />
            </Box>
          </Box>
        )}

        {/* Form */}
        <Box component="form" onSubmit={handlePost}>
          <TextField
            fullWidth size="small"
            placeholder="Onderwerp (optioneel)"
            value={subject} onChange={(e) => setSubject(e.target.value)}
            inputProps={{ maxLength: MAX_SUBJECT }}
            sx={{ mb: 1, ...stitchInputSx }}
          />
          <TextField
            fullWidth multiline minRows={3} maxRows={8} size="small"
            placeholder="Schrijf een update voor de klant…"
            value={body} onChange={(e) => setBody(e.target.value)}
            inputProps={{ maxLength: MAX_BODY }}
            sx={{ mb: 1, ...stitchInputSx }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outlineVariant }}>
              {body.length} / {MAX_BODY}
            </Typography>
            <Box
              component="button"
              type="submit"
              disabled={posting || !body.trim()}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75,
                px: 1.75, py: 0.75, borderRadius: '0.5rem',
                bgcolor: ACCENT, color: '#fff',
                fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600,
                border: 'none', cursor: 'pointer',
                transition: 'all 0.15s',
                boxShadow: posting ? 'none' : '0 1px 2px rgba(124, 95, 223, 0.25)',
                '&:hover:not(:disabled)': { bgcolor: '#6a4ec9', transform: 'translateY(-1px)', boxShadow: '0 3px 8px rgba(124, 95, 223, 0.3)' },
                '&:active:not(:disabled)': { transform: 'translateY(0)' },
                '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
              }}
            >
              {posting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Icon name="send" size={16} sx={{ color: '#fff' }} />}
              {posting ? 'Versturen…' : 'Plaats bericht'}
            </Box>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ height: 1, bgcolor: 'rgba(45,51,53,0.06)', my: 2.5 }} />

        {/* List header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, color: T.outlineVariant, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Eerder geplaatst
          </Typography>
          <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(45,51,53,0.06)' }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', color: T.outlineVariant }}>
            {messages.length}
          </Typography>
        </Box>

        {/* List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} sx={{ color: T.outline }} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Icon name="chat_bubble_outline" size={36} sx={{ color: T.outlineVariant, display: 'block', margin: '0 auto 8px' }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', fontWeight: 500, color: T.outline }}>
              Nog geen berichten
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outlineVariant, mt: 0.25 }}>
              Plaats het eerste bericht hierboven
            </Typography>
          </Box>
        ) : (
          <Box>
            {messages.map((m) => (
              <Box
                key={m.id}
                sx={{
                  display: 'flex', gap: 1.25, py: 1.25, px: 1,
                  borderRadius: '0.5rem',
                  transition: 'background 0.15s',
                  '&:hover': { bgcolor: 'rgba(45,51,53,0.02)' },
                  '&:hover .delete-btn': { opacity: 1 },
                }}
              >
                <Box sx={{
                  width: 28, height: 28, borderRadius: '0.4rem', flexShrink: 0,
                  bgcolor: ACCENT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mt: 0.25,
                }}>
                  <Icon name="forum" size={14} sx={{ color: ACCENT }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, flexWrap: 'wrap', mb: 0.25 }}>
                    {m.subject && (
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', fontWeight: 600, color: T.text }}>
                        {m.subject}
                      </Typography>
                    )}
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outline }}>
                      {m.author_email}
                    </Typography>
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: T.outlineVariant }} />
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outlineVariant }}>
                      {formatDate(m.created_at)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {m.body}
                  </Typography>
                </Box>
                {m.author_email === currentUserEmail && (
                  <Box
                    className="delete-btn"
                    onClick={() => handleDelete(m.id)}
                    title="Verwijderen"
                    sx={{
                      opacity: 0, transition: 'opacity 0.15s',
                      cursor: 'pointer',
                      width: 28, height: 28, borderRadius: '0.4rem',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: T.outline,
                      flexShrink: 0,
                      '&:hover': { bgcolor: '#fef2f2', color: '#dc2626' },
                    }}
                  >
                    <Icon name="delete_outline" size={16} />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
